import { useEffect, useMemo, useState } from "react";
import { fhirClient } from "src/App";
import { Bundle, Patient } from "src/careflow/ph-core/fhir-client/resources";
import { getReferenceId } from "src/utils/fhir";
import useAuth from "src/careflow/ph-core/authentication/hook/useAuth";

const usePatientsOrgValidation = (patientsFhirIds: string[] | undefined) => {
  const [organizationToValidate, setOrganizationToValidate] = useState<Record<string, string>[] | null>(null);
  const [patientsOrgsValid, setPatientsOrgsValid] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const user = useAuth();
  const { getSelectedOrg, getCurrentUser } = user;

  const userOrgs = useMemo(() => 
    getCurrentUser().organizations, 
  [getSelectedOrg, getCurrentUser]);


  useEffect(() => {
    const handleGetFhirResource = async (currentIdentifierValues: string[]) => {
      setIsLoading(true);
      try {
        const response = await fhirClient.search<Patient>(
          `Patient?_id=${currentIdentifierValues.join(",")}`
        );

        const managementOrganizations =
          response?.entry?.reduce((arr: Record<string, string>[], { resource }) => {
            const managementOrganizationId = getReferenceId(resource?.managingOrganization?.reference);
            const patientId = resource?.id;
        
            if (patientId && managementOrganizationId) {
              arr.push({ patientId , managementOrganizationId });
            }
        
            return arr;
          }, []) ?? [];

        setOrganizationToValidate(managementOrganizations);
      } catch (error) {
        setOrganizationToValidate([]); 
      }
    };

    if (patientsFhirIds && patientsFhirIds.length > 0) {
      handleGetFhirResource(patientsFhirIds);
    } else {
      setOrganizationToValidate(null);
    }
  }, [patientsFhirIds]);

  useEffect(() => {
    if (!Array.isArray(organizationToValidate)) {
      setPatientsOrgsValid(null);
      setIsLoading(false);
      return;
    }

    if (organizationToValidate.length === 0 || userOrgs?.length === 0) {
      setPatientsOrgsValid(null);
      setIsLoading(false);
      return;
    }

    const transformed = organizationToValidate.map(({ patientId, managementOrganizationId }) => {
      const isCheckOrgValidate = userOrgs.includes(managementOrganizationId);
      return { patientId, isCheckOrgValidate };
    });

    setPatientsOrgsValid(transformed);
    setIsLoading(false);
  }, [organizationToValidate, userOrgs]);

  return { isLoading, patientsOrgsValid };
};

export default usePatientsOrgValidation;